import { datadogRum } from '@datadog/browser-rum';
import { alpha, useTheme } from '@mui/material';
import debounce from '@mui/utils/debounce';
import { AutocompleteNoResults, toast, useIsMobile } from '@side/pantry';
import { useMap } from '@vis.gl/react-google-maps';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  selectedGeocodeLocation,
  selectedGroupKey,
  selectedPartnerMapTeamId,
  teamDetailPanelVisible,
  viewAllTeamId,
} from '@/common/atoms/community';
import { AutocompleteSearchBox } from '@/components/AutocompleteSearchBox';
import { unique } from '@/helpers';
import {
  type GeocodeResult,
  useGeocoder,
} from '@/views/Community/views/CommunityListings/CommunityListings.helpers';
import type { Location } from '@/views/Community/views/CommunityListings/CommunityListings.state';
import {
  type DropdownOption,
  useDefaultOptions,
} from '@/views/Community/views/CommunityListings/components/FilterButton/FilterDialog/LocationAutocomplete/LocationAutocomplete.helpers';
import { Option } from '@/views/Community/views/CommunityListings/components/FilterButton/FilterDialog/LocationAutocomplete/Option';
import { AutofillProvider } from '@/views/Coversheet/components/PlacesAutofill/PlacesAutofill.helpers';
import { useAutofillActionProvider } from '@/views/Coversheet/hooks/useAutofillActionProvider';
import { TrackCommunityEvent } from '$analytics/useTrackAnalytics/domains/community/events';
import { useTrackCommunityAnalytics } from '$analytics/useTrackAnalytics/domains/community/useTrackCommunityAnalytics';
import { DEFAULT_LOCALE_IDS, DEFAULT_ZOOM } from '../../../constants';
import { useGetPartnerInformation } from '../../../hooks/useGetPartnerInformation';
import { useShowAllTeamLocations } from '../../../hooks/useShowAllTeamLocations';
import { useTeamNameSearch } from '../../../hooks/useTeamNameSearch';
import type { InitialLocation } from '../../../types';
import { moveMapPinIntoView } from '../../../utils/moveMapPinIntoView';
import { TeamOption } from '../TeamOption';
import { buildDropdownOptions, getResultType } from './helpers';

export type GeocodeLocation = Location & GeocodeResult;

const FEATURE_TYPES = [
  'administrative_area_level_1', // state
  'administrative_area_level_2', // county
  'locality', // city
  'postal_code',
];
const HEADER_HEIGHT = 168;

export interface Props {
  getTeams: (
    map: google.maps.Map | null,
    initialCenter: InitialLocation,
  ) => Promise<void>;
  initialCenter: InitialLocation;
}

export const LocationAutocomplete = ({ getTeams, initialCenter }: Props) => {
  const defaultOptions = useDefaultOptions();
  const [autofillOptions, setAutofillOptions] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchPending, setSearchPending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useAtom(
    selectedGeocodeLocation,
  );
  const map = useMap();
  const theme = useTheme();
  const trackEvent = useTrackCommunityAnalytics();
  const [teamNameSearch] = useTeamNameSearch();
  const setSelectedTeamId = useSetAtom(selectedPartnerMapTeamId);
  const setSelectedGroupKey = useSetAtom(selectedGroupKey);
  const setTeamDetailPanelVisible = useSetAtom(teamDetailPanelVisible);
  const isMobile = useIsMobile('md');
  const [open, setOpen] = useState(false);
  const [viewAllTeam, setViewAllTeam] = useAtom(viewAllTeamId);
  const showAllTeamLocationsCallback = useShowAllTeamLocations();
  const { partnerDetails: viewAllPartnerDetails } = useGetPartnerInformation({
    teamId: viewAllTeam ?? null,
  });
  const { geocode } = useGeocoder();

  const selectLocation = useCallback(
    (location: GeocodeLocation | null) => {
      setSelectedLocation(location);

      if (!map) return;

      const selectedMapStyles = {
        strokeColor: theme.palette.primary.main,
        strokeOpacity: 1.0,
        strokeWeight: 3,
        fillColor: alpha(theme.palette.primary.main, 0.08),
        fillOpacity: 0,
      };

      const defaultMapStyles = {
        fillColor: theme.palette.common.black,
        fillOpacity: 0.05,
      };

      if (map.getRenderingType() === 'VECTOR') {
        FEATURE_TYPES.forEach((geocodeType) => {
          const featureLayer = map.getFeatureLayer(
            geocodeType.toUpperCase() as google.maps.FeatureType,
          );

          if (
            location?.types.includes(geocodeType) &&
            featureLayer?.isAvailable
          ) {
            featureLayer.style = ({ feature }) => {
              const placeId = 'placeId' in feature ? feature.placeId : null;
              return placeId === location.place_id
                ? selectedMapStyles
                : defaultMapStyles;
            };
          } else {
            // Clear existing styles
            featureLayer.style = null;
          }
        });
      }

      if (location) {
        // Move map to selected location
        map.fitBounds(location.geometry.viewport);

        if (DEFAULT_LOCALE_IDS.includes(location.id)) {
          map.setZoom(DEFAULT_ZOOM);
        }

        // Only fetch teams if we're not in view all mode
        if (!viewAllTeam) {
          void getTeams(map, initialCenter);
        }
      }
    },
    [setSelectedLocation, map, theme, getTeams, initialCenter, viewAllTeam],
  );

  const searchRestrictions = useMemo(
    () => ({
      typesOfPredictionsToUse: ['geocode'],
      restrictions: {
        country: 'US',
      },
    }),
    [],
  );
  const searchProvider = useAutofillActionProvider(
    AutofillProvider.googlePlacesSearch,
    searchRestrictions,
  );

  const handleAutocompleteChange = async (newValue: Location) => {
    setViewAllTeam(undefined);
    setSelectedTeamId(undefined);
    setSelectedGroupKey(undefined);
    setTeamDetailPanelVisible(false);

    if (!newValue) {
      selectLocation(null);
      return;
    }

    setLoading(true);

    if (newValue.id.startsWith('team:')) {
      setSelectedTeamId(newValue.id.replace('team:', ''));
      setTeamDetailPanelVisible(!isMobile);
      if (map && newValue.latitude && newValue.longitude) {
        map.setCenter({ lat: newValue.latitude, lng: newValue.longitude });
        moveMapPinIntoView(map, 500);
      }
    }

    const geocodeRequest =
      newValue.latitude && newValue.longitude
        ? {
            location: { lat: newValue.latitude, lng: newValue.longitude },
          }
        : { placeId: newValue.id };

    try {
      const result = await geocode(geocodeRequest);
      selectLocation({
        ...newValue,
        ...result,
      });

      trackEvent(TrackCommunityEvent.PartnerMapSelectSearchDropdownOption, {
        searchString: searchTerm,
        resultType: getResultType(newValue.id),
        resultValue: [result.latitude, result.longitude],
        resultTeamIds: newValue.id.startsWith('team:')
          ? [newValue.id.replace('team:', ''), newValue.mailingAddress?.id]
          : null,
      });
    } catch (err) {
      datadogRum.addError('Failed to select a partner map location dropdown', {
        err,
        selectedLocationId: newValue.id,
      });
      toast('error', 'An error occurred, please refresh and try again');
    } finally {
      setLoading(false);
    }
  };

  const handleOnInputChange = useCallback(
    async (input: string, reason: string) => {
      setSearchPending(false);
      if (reason === 'reset') return;
      if (!input.length) {
        setAutofillOptions([]);
        return;
      }
      setLoading(true);
      const [locationPredictions, teamNameResult] = await Promise.all([
        new Promise<DropdownOption[]>((resolve) => {
          searchProvider(
            input,
            (predictions: google.maps.places.AutocompletePrediction[]) => {
              resolve(
                predictions.map((prediction) => ({
                  id: prediction.place_id,
                  label: prediction.structured_formatting.main_text,
                  secondaryLabel:
                    prediction.structured_formatting.secondary_text,
                  group: 'Locations',
                })),
              );
            },
          );
        }),
        teamNameSearch(input),
      ]);

      const dropdownOptions = buildDropdownOptions(
        locationPredictions,
        teamNameResult.options,
        teamNameResult.injectedParentCount,
      );
      setAutofillOptions(dropdownOptions);
      setLoading(false);
    },
    [
      setAutofillOptions,
      setLoading,
      searchProvider,
      teamNameSearch,
      setSearchPending,
    ],
  );

  const debouncedOnInputChange = useMemo(
    () => debounce(handleOnInputChange, 300),
    [handleOnInputChange],
  );

  // Deselect geocode location when "view all locations" is clicked
  useEffect(() => {
    if (viewAllTeam) {
      selectLocation(null);
    }
  }, [viewAllTeam, selectLocation]);

  const autocompleteValue = useMemo(() => {
    return viewAllTeam
      ? {
          id: 'viewAll',
          label: [
            viewAllPartnerDetails?.legalName,
            viewAllPartnerDetails?.alias,
          ]
            .filter((v) => !!v)
            .filter(unique)
            .join(', '),
        }
      : selectedLocation;
  }, [
    selectedLocation,
    viewAllTeam,
    viewAllPartnerDetails?.legalName,
    viewAllPartnerDetails?.alias,
  ]);

  let options: DropdownOption[] = [];
  if (autofillOptions.length) {
    options = autofillOptions;
  } else if (
    !searchTerm ||
    defaultOptions.some((option) => option?.id === selectedLocation?.id)
  ) {
    options = defaultOptions;
  }

  return (
    <AutocompleteSearchBox
      loading={loading}
      options={options}
      value={autocompleteValue as DropdownOption}
      onChange={(_, newValue) => {
        void handleAutocompleteChange(newValue);
      }}
      onInputChange={(_, newInputValue, reason) => {
        const currentLocationClicked =
          reason === 'reset' && newInputValue === 'Current Location';
        const newSearchTerm = currentLocationClicked ? '' : newInputValue;
        setSearchTerm(newSearchTerm);
        setSearchPending(true);
        void debouncedOnInputChange(newSearchTerm, reason);
      }}
      filterOptions={(x) => x}
      inputValue={searchTerm === 'Current Location' ? undefined : searchTerm}
      TextFieldProps={{
        placeholder: isMobile
          ? 'Search by team or location'
          : 'Search by team, address, city, state, county, or zip code',
      }}
      disablePortal
      slotProps={{
        listbox: {
          sx: {
            '.MuiAutocomplete-option': {
              py: 1,
            },
            maxHeight: `min(calc(100dvh - ${HEADER_HEIGHT}px), 500px)`,
          },
        },
      }}
      groupBy={(option) =>
        typeof option?.group === 'string' ? option.group : ''
      }
      renderOption={(optionProps, option) => {
        if (option?.id.startsWith('team:')) {
          const teamId = option.id.replace('team:', '');
          return (
            <TeamOption
              option={option}
              searchTerm={searchTerm}
              {...optionProps}
              key={option?.id}
              onClickViewAll={() => {
                setOpen(false);
                void showAllTeamLocationsCallback(teamId);
                trackEvent(
                  TrackCommunityEvent.PartnerMapViewAllLocationsDropdownClick,
                  {
                    resultTeamIds: [teamId, option.mailingAddress?.id],
                    resultType: 'allLocations',
                    searchString: searchTerm,
                    resultValue: [
                      option.mailingAddress?.geolocationLatitude,
                      option.mailingAddress?.geolocationLongitude,
                    ],
                  },
                );
              }}
            />
          );
        }
        return (
          <Option
            option={option}
            searchTerm={searchTerm}
            {...optionProps}
            key={option?.id}
          />
        );
      }}
      noOptionsText={
        loading || searchPending || !searchTerm || viewAllTeam ? (
          ''
        ) : (
          <AutocompleteNoResults />
        )
      }
      sx={{
        '& ~ .MuiAutocomplete-popper .MuiAutocomplete-noOptions:empty': {
          display: 'none',
        },
        '.MuiAutocomplete-clearIndicator': {
          display: 'none',
          mr: 1,
        },
        '&.Mui-focused .MuiAutocomplete-clearIndicator': {
          display: 'inline-flex',
        },
        '.MuiAutocomplete-endAdornment': {
          mr: 0,
        },
      }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
    />
  );
};
